import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { FormField, HStack, Text, useCustomMask, VStack } from '@direct-frontend/components';
import { waitForSelector } from '@direct-frontend/modules-store';
import { useField, useFormContext, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { usePhoneMask } from '@adv-frontend/yastore-modules/hooks';
import { getLoadingOrder, getOrderQuery, useLoadingOrder } from '@/entities/order';
import { DrawerBottomFixed, DrawerButton } from '@/shared/components/Drawer';
import { MaskedTextInput, TextInput } from '@/shared/components/TextInput';
import { getRequestContext } from '@/shared/lib/requestContext';
import { shortenPhone } from '../../lib/shortenPhone';
import { clientForm } from '../../model/clientForm';
import { buildErrorMessage, CLIENT_FORM_ID } from '../../model/constants';
import { type ClientFormProps } from '../../types';
import styles from './ClientForm.module.css';

export const ClientForm: FC<ClientFormProps & { ozonRegistrationButton: ReactNode }> = (props) => {
    const phoneMask = usePhoneMask();
    const { goNext, onClose, onSubmit, ozonRegistrationButton } = props;
    const { fields } = useFormInstance({ form: clientForm, id: CLIENT_FORM_ID });

    const fieldLastName = useField(fields.lastName);
    const fieldFirstName = useField(fields.firstName);
    const fieldEmail = useField(fields.email);
    const fieldPhone = useField(fields.phone);

    const emailMaskProps = useMemo(() => {
        return {
            mask: [...Array(255).fill(/[^ \s]/)],
            transformToUnmasked: (value: string) => value.trim(),
        };
    }, []);

    const emailMask = useCustomMask(emailMaskProps);

    return (
        <VStack className={styles.root}>
            <VStack className={styles.vertical}>
                <FormField
                    size="s"
                    className={styles.formField}
                    errorMessage={buildErrorMessage(fieldLastName.state.errors)}
                >
                    {(fieldProps) => <TextInput {...fieldProps} {...fieldLastName.getProps()} label="Фамилия *" />}
                </FormField>
                <FormField
                    size="s"
                    className={styles.formField}
                    errorMessage={buildErrorMessage(fieldFirstName.state.errors)}
                >
                    {(fieldProps) => <TextInput {...fieldProps} {...fieldFirstName.getProps()} label="Имя *" />}
                </FormField>
                <FormField
                    size="s"
                    className={styles.formField}
                    errorMessage={buildErrorMessage(fieldEmail.state.errors)}
                >
                    {(fieldProps) => (
                        <MaskedTextInput
                            {...fieldProps}
                            {...fieldEmail.getProps()}
                            label="Email *"
                            inputMode="email"
                            maskConfig={emailMask}
                        />
                    )}
                </FormField>
                <HStack alignItems="center" gap="16">
                    <FormField
                        size="s"
                        className={styles.formField}
                        errorMessage={buildErrorMessage(fieldPhone.state.errors)}
                        description={ozonRegistrationButton && 'К номеру не привязан Ozon ID'}
                    >
                        {(fieldProps) => (
                            <MaskedTextInput
                                {...fieldProps}
                                {...fieldPhone.getProps()}
                                label="Телефон *"
                                placeholder="+7 (ХХХ) ХХХ-ХХ-ХХ"
                                maskConfig={phoneMask}
                                inputMode="tel"
                            />
                        )}
                    </FormField>
                    {ozonRegistrationButton}
                </HStack>
            </VStack>

            <VStack className={styles.buttonWrapper}>
                <DrawerBottomFixed>
                    <RenderDrawerButton goNext={goNext} onClose={onClose} onSubmit={onSubmit} />
                </DrawerBottomFixed>
            </VStack>
        </VStack>
    );
};

type RenderDrawerButtonProps = ClientFormProps;

const RenderDrawerButton: FC<RenderDrawerButtonProps> = ({ goNext, onClose, onSubmit: propOnSubmit }) => {
    const { isOrderUpdating } = useLoadingOrder();
    const { needPhoneVerification } = useFormContext({ form: clientForm, id: CLIENT_FORM_ID });
    const { canSubmit } = useFormState({ form: clientForm, id: CLIENT_FORM_ID });
    const { fields, revalidate, submit } = useFormInstance({ form: clientForm, id: CLIENT_FORM_ID });
    const fieldPhone = useField(fields.phone);
    const phone = fieldPhone.value;
    const phoneVerified = useField(fields.phoneVerified).value;

    const onSubmit = useCallback(async () => {
        revalidate();

        const values = await submit();

        if (values) {
            propOnSubmit(values);
        }

        return values;
    }, [revalidate, submit, propOnSubmit]);

    if (!needPhoneVerification || phone === phoneVerified) {
        return (
            <DrawerButton
                className={styles.drawerButton}
                isDisabled={!canSubmit || isOrderUpdating}
                onClick={async () => {
                    if (!(await onSubmit())) {
                        return;
                    }

                    await waitForSelector(() => !getLoadingOrder().isOrderUpdating);

                    // После обновления снэпшота в кэш записалось новое значение `need_phone_verification`,
                    // прочитаем его чтобы узнать потребуется ли верификация номера на следующем шаге или можем закрывать
                    const { orderId, signature, storeSlug } = getRequestContext();
                    const { client } = await getOrderQuery({ order_id: orderId, signature, store_slug: storeSlug });

                    if (client.need_phone_verification) {
                        goNext();
                    } else {
                        onClose();
                    }
                }}
            >
                Далее
            </DrawerButton>
        );
    }

    if (phone.match(/^\+7\d{10}$/)) {
        return (
            <DrawerButton
                className={styles.drawerButton}
                isDisabled={!canSubmit || isOrderUpdating}
                onClick={async () => {
                    if (!(await onSubmit())) {
                        return;
                    }

                    // Чтобы начать процесс верификации номера телефона нужно дождаться когда этот номер запишется на бэке
                    await waitForSelector(() => !getLoadingOrder().isOrderUpdating);
                    goNext();
                }}
                addonStart={<Text color="white">Далее</Text>}
                addonEnd={<Text className={styles.invertedSecondaryText}>sms на номер {shortenPhone(phone)}</Text>}
            />
        );
    }

    return (
        <DrawerButton className={styles.drawerButton} isDisabled={!canSubmit} onClick={revalidate}>
            Далее
        </DrawerButton>
    );
};
